import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

/**
 * Catches the Chrome/Edge/Android `beforeinstallprompt` event and exposes
 * a sticker-style install button. Auto-hides if already installed or dismissed.
 */
export default function InstallPrompt() {
  const [evt, setEvt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setEvt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setEvt(null);
      try { localStorage.setItem("xseed.installed", "1"); } catch (e) { /* ignore */ }
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // Already installed (running standalone)?
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) setInstalled(true);

    try {
      if (localStorage.getItem("xseed.installed") === "1") setInstalled(true);
      if (localStorage.getItem("xseed.install.dismissed") === "1") setDismissed(true);
    } catch (e) { /* ignore */ }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || dismissed || !evt) return null;

  const install = async () => {
    try {
      evt.prompt();
      const { outcome } = await evt.userChoice;
      if (outcome === "accepted") {
        setInstalled(true);
        try { localStorage.setItem("xseed.installed", "1"); } catch (e) { /* ignore */ }
      } else {
        setDismissed(true);
        try { localStorage.setItem("xseed.install.dismissed", "1"); } catch (e) { /* ignore */ }
      }
      setEvt(null);
    } catch (e) { /* ignore */ }
  };

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("xseed.install.dismissed", "1"); } catch (e) { /* ignore */ }
  };

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] max-w-md w-[calc(100%-2rem)] card-x card-magenta bg-[#FAF6E8] p-3 flex items-center gap-3 animate-fade-in"
      style={{ animation: "fadeUp .35s ease-out" }}
    >
      <div className="w-11 h-11 rounded-2xl bg-[#15FF73] border-2 border-[#0B0F0C] flex items-center justify-center flex-shrink-0">
        <Smartphone size={18} strokeWidth={2.4} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display text-base font-extrabold leading-none mb-1">Install X-Seed</div>
        <div className="text-[11px] font-mono-x font-bold uppercase tracking-widest opacity-65">One tap · home-screen app · zero app-store</div>
      </div>
      <button
        data-testid="install-prompt-install"
        onClick={install}
        className="btn-x btn-primary !py-2 !px-3 !text-xs flex-shrink-0"
      >
        <Download size={12} strokeWidth={2.6} /> Install
      </button>
      <button
        data-testid="install-prompt-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="w-8 h-8 rounded-full border-2 border-[#0B0F0C] flex items-center justify-center hover:bg-[#FF1A8C] hover:text-white flex-shrink-0"
      >
        <X size={11} strokeWidth={2.4} />
      </button>
    </div>
  );
}
